import { useApiInstance } from 'api';
import { parseCookies } from 'nookies';
import React, { useState } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import FieldSet from '@/components/FieldSet/FieldSet';
import Label from '@/components/Form/Label/Label';
import PhoneField from '@/components/Form/PhoneInput/PhoneField';
import { CheckEmail, CheckRequired, composeValidators, MapServerErrors } from '@/components/FormAdapter/FormAdapter';
import Grid from '@/components/Grid/Grid';
import GridWrap from '@/components/Grid/GridWrap';
import Link from '@/components/Link/Link';
import Paper from '@/components/Paper/Paper';
import Typography from '@/components/Typography/Typography';
import Button from '@/components/v2/Button/Button';
import { InputFormField } from '@/components/v2/FinalForm/Fields/InputFormField';
import Text from '@/components/v2/Text/Text';
import { showNotifyNew } from '@/models/Notify';
import { TRACKED_QUERY_PARAMS } from '@/pages/_app';
import { OUTSTAFF } from '@/utils/base';

import EmptyCheckMail from '../Empty/EmptyCheckMail';

interface Values {
  company: {
    name: string;
    link: string;
  };
  user: {
    name: string;
    post: string;
    email: string;
    phone: string;
  };
  offer: boolean;
  eula: boolean;
}

const RegisterCompanyForm: React.FC = () => {
  const intl = useIntl();
  const api = useApiInstance();
  const dispatch = useDispatch();
  const [done, setDone] = useState<string | undefined>('');

  const register = async (values: Values) => {
    // Получаем трекинговые куки
    const cookies = parseCookies();
    const trackingParams: any = {};
    TRACKED_QUERY_PARAMS.forEach((paramName) => {
      if (cookies[`tr_${paramName}`]) {
        trackingParams[paramName] = cookies[`tr_${paramName}`];
      }
    });

    let result;
    try {
      result = await api.post('/company/register', { ...values, ...trackingParams, offer: true, eula: true });
    } catch (error) {
      result = error;
    }
    return result;
  };
  const onSubmit = async (values: Values) => {
    const result = await register(values);
    let errors;
    if (result.data) {
      setDone(values.user.email);
    } else if (result.response && result.response.status === 422) {
      errors = MapServerErrors(result.response.data.errors);
    } else {
      showNotifyNew(dispatch, {
        content: `Server error:
          ${result.statusCode ? result.statusCode : ''}
          ${result.message ? result.message : ''}`,
        type: 'error',
      });
    }
    return errors;
  };

  const singleRowInput = [
    {
      title: intl.formatMessage({ id: 'app.form.company.name.label' }),
      name: 'company.name',
      type: 'text',
      validate: CheckRequired,
    },
    {
      title: intl.formatMessage({ id: 'app.form.company.link.label' }),
      name: 'company.link',
      type: 'text',
      validate: CheckRequired,
    },
  ];

  const doubleRowInput = [
    [
      {
        title: intl.formatMessage({ id: 'app.form.yourname.label' }),
        name: 'user.name',
        type: 'text',
        validate: CheckRequired,
        component: InputFormField,
      },
      {
        title: intl.formatMessage({ id: 'app.form.position.label' }),
        name: 'user.post',
        type: 'text',
        validate: CheckRequired,
        component: InputFormField,
      },
    ],
    [
      {
        title: intl.formatMessage({ id: 'app.form.email.label' }),
        name: 'user.email',
        type: 'email',
        validate: composeValidators(CheckRequired, CheckEmail),
        component: InputFormField,
      },
      {
        title: intl.formatMessage({ id: 'app.form.phone.label' }),
        name: 'user.phone',
        type: 'text',
        validate: CheckRequired,
        component: PhoneField,
      },
    ],
  ];

  return (
    <>
      {!done ? (
        <Paper>
          <div className="geecko-registerform-company-bg" />
          <div className="geecko-registerform-block">
            <Text variant="h1" tag="h1">
              <FormattedMessage id="app.register.company.pagetitle" />
            </Text>
            {!OUTSTAFF && (
              <Typography variant="body3" bold component="div" spacing={5}>
                {`${intl.formatMessage({ id: 'app.register.areyoudeveloper' })} `}
                <Link to="/register/candidate">{intl.formatMessage({ id: 'app.signup' })}</Link>
              </Typography>
            )}
            <Form
              onSubmit={onSubmit}
              render={({ handleSubmit, form, submitting, pristine, values }) => (
                <form onSubmit={handleSubmit} style={{ margin: '30px 0 0' }}>
                  <Grid container spacing={5}>
                    <Grid item xs={12} sm={8}>
                      {singleRowInput.map((field) => (
                        <FieldSet spacing={5} key={field.name}>
                          <Label>{field.title}</Label>
                          <InputFormField name={field.name} type={field.type} validate={field.validate} />
                        </FieldSet>
                      ))}
                      {doubleRowInput.map((row, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <GridWrap key={idx}>
                          <Grid container spacing={3}>
                            {row.map((field) => {
                              const InputComponent = field.component;
                              return (
                                <Grid item xs={12} sm={6} key={field.name}>
                                  <FieldSet spacing={5}>
                                    <Label>{field.title}</Label>
                                    <InputComponent name={field.name} type={field.type} validate={field.validate} />
                                  </FieldSet>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </GridWrap>
                      ))}
                      {/* <FieldSet spacing={2}>
                        <Field<boolean>
                          name="eula"
                          type="checkbox"
                          component={PlainCheckboxAdapter}
                          validate={CheckRequired}
                          content={
                            <>
                              {intl.formatMessage({ id: 'app.form.agreement.familiarized' })}
                              <LinkPersonalDataProcessing
                                title={intl.formatMessage({ id: 'app.form.agreement.argee' })}
                              />
                              {` ${intl.formatMessage({ id: 'app.form.agreement.with' })} `}
                              <LinkPrivacyPolicy
                                title={intl.formatMessage({ id: 'app.form.agreement.privacypolicy' })}
                              />
                            </>
                          }
                        />
                      </FieldSet> */}
                      {/* <FieldSet spacing={5}>
                        <Field<boolean>
                          name="offer"
                          type="checkbox"
                          component={PlainCheckboxAdapter}
                          validate={CheckRequired}
                          content={
                            <>
                              {`${intl.formatMessage({ id: 'app.form.agreement.accept' })} `}
                              <LinkPublicOffer title={intl.formatMessage({ id: 'app.form.agreement.publicoffer' })} />
                            </>
                          }
                        />
                      </FieldSet> */}
                      <GridWrap>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <Button size="lg" type="submit" isWide disabled={submitting}>
                              <FormattedMessage id="app.register" />
                            </Button>
                          </Grid>
                          <Grid item xs={12} sm={6} />
                        </Grid>
                      </GridWrap>
                    </Grid>
                    <Grid item xs={12} sm={4} />
                  </Grid>
                </form>
              )}
            />
          </div>
        </Paper>
      ) : (
        <EmptyCheckMail text={intl.formatMessage({ id: 'app.register.success.message' }, { email: done })} />
      )}
    </>
  );
};

export default RegisterCompanyForm;
